import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LifeBuoy, Send, X, ShieldAlert, Clock, ShieldCheck, Lock } from "lucide-react";

/**
 * Support ticket panel. Any player can open a ticket to appeal a moderation
 * decision or ask a question. Admins reply from the AdminPanel.
 *
 * Shown as a slide-out drawer, opened from the World view.
 */
export default function SupportTickets({ onClose }) {
  const [tickets, setTickets] = useState([]);
  const [me, setMe] = useState(null);
  const [selected, setSelected] = useState(null);
  const [newSubj, setNewSubj] = useState("");
  const [newBody, setNewBody] = useState("");
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const [t, m] = await Promise.all([api.ticketsMine(), api.moderationMe()]);
      setTickets(t);
      setMe(m);
      if (selected) setSelected(t.find(x => x.ticket_id === selected.ticket_id) || null);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { refresh(); const t = setInterval(refresh, 10000); return () => clearInterval(t); }, []);

  const openTicket = async () => {
    if (!newSubj.trim() || !newBody.trim() || busy) return;
    setBusy(true);
    try {
      const t = await api.ticketOpen(newSubj.trim(), newBody.trim());
      toast.success("Ticket opened");
      setNewSubj(""); setNewBody("");
      await refresh();
      setSelected(t);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const sendReply = async () => {
    if (!reply.trim() || busy || !selected) return;
    setBusy(true);
    try {
      await api.ticketReply(selected.ticket_id, reply.trim());
      setReply("");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const muted = me?.muted;
  const permanent = me?.permanent;
  const warnings = me?.warnings || 0;

  return (
    <div
      className="fixed inset-0 z-40 bg-black/80 backdrop-blur-sm flex items-stretch justify-end"
      data-testid="support-drawer"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-[#050505] border-l border-[#262626] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-[#262626] flex items-center gap-2 sticky top-0 bg-[#050505] z-10">
          <LifeBuoy className="w-4 h-4 text-[#B89C3A]" strokeWidth={1.5} />
          <span className="font-heading text-sm tracking-[0.3em] uppercase text-[#E2DFD8]">Support</span>
          <button
            data-testid="support-close"
            onClick={onClose}
            className="ml-auto text-[#666] hover:text-[#E2DFD8]"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        {/* Moderation status */}
        <div className="p-4 space-y-3">
          <div
            className="panel p-3 space-y-1"
            data-testid="mod-status"
            data-muted={muted ? "1" : "0"}
            data-permanent={permanent ? "1" : "0"}
          >
            <div className="flex items-center gap-2">
              {muted ? (
                <Lock className="w-3 h-3 text-[#8A0303]" strokeWidth={1.5} />
              ) : warnings > 0 ? (
                <ShieldAlert className="w-3 h-3 text-[#B89C3A]" strokeWidth={1.5} />
              ) : (
                <ShieldCheck className="w-3 h-3 text-[#6E8B3D]" strokeWidth={1.5} />
              )}
              <span className="font-heading text-[10px] tracking-[0.3em] uppercase text-[#8c8c8c]">
                Your Standing
              </span>
            </div>
            <div className="font-body text-xs text-[#E2DFD8]">
              {muted
                ? (permanent
                    ? "You are permanently muted. Open a ticket below to appeal."
                    : `You are muted until ${new Date(me.until).toLocaleString()}. Open a ticket below to appeal.`)
                : warnings > 0
                  ? `Warnings: ${warnings}/3`
                  : "No warnings. Walk in peace."}
            </div>
          </div>

          {/* Existing tickets list */}
          <div className="panel">
            <div className="px-3 py-2 border-b border-[#262626] font-heading text-[10px] tracking-[0.3em] uppercase text-[#8c8c8c]">
              Your Tickets
            </div>
            <div className="max-h-40 overflow-y-auto">
              {tickets.length === 0 && (
                <p className="italic text-[#666] text-xs p-3">No tickets yet.</p>
              )}
              {tickets.map(t => {
                const isSel = selected?.ticket_id === t.ticket_id;
                return (
                  <button
                    key={t.ticket_id}
                    data-testid={`ticket-row-${t.ticket_id}`}
                    onClick={() => setSelected(t)}
                    className={`w-full text-left px-3 py-2 border-b border-[#1a1a1a] hover:bg-[#0e0e0e] ${isSel ? "bg-[#0e0e0e]" : ""}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-body text-xs text-[#E2DFD8] truncate flex-1">{t.subject}</span>
                      <span className={`font-mono text-[9px] tracking-widest uppercase ${
                        t.status === "closed" ? "text-[#666]" :
                        t.status === "answered" ? "text-[#6E8B3D]" : "text-[#B89C3A]"
                      }`}>{t.status}</span>
                    </div>
                    <div className="font-mono text-[9px] text-[#555] mt-0.5">{t.messages.length} msg</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Selected ticket thread */}
          {selected ? (
            <div className="panel" data-testid="ticket-thread">
              <div className="px-3 py-2 border-b border-[#262626] flex items-center">
                <span className="font-heading text-xs tracking-widest uppercase text-[#E2DFD8] flex-1 truncate">
                  {selected.subject}
                </span>
                <button onClick={() => setSelected(null)} className="text-[#666] hover:text-[#E2DFD8]">
                  <X className="w-3 h-3" strokeWidth={1.5} />
                </button>
              </div>
              <div className="max-h-56 overflow-y-auto p-3 space-y-2">
                {selected.messages.map((m, i) => (
                  <div
                    key={i}
                    className={`text-xs font-body p-2 border-l-2 ${m.from === "admin" ? "border-[#B89C3A] bg-[#0a0a0a]" : "border-[#262626]"}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`font-heading text-[10px] tracking-widest uppercase ${m.from === "admin" ? "text-[#E5C34A]" : "text-[#4A7BC7]"}`}>
                        {m.from === "admin" ? `Admin · ${m.author}` : m.author}
                      </span>
                      <Clock className="w-2.5 h-2.5 text-[#555]" strokeWidth={1.5} />
                      <span className="font-mono text-[9px] text-[#555]">
                        {new Date(m.at).toLocaleString()}
                      </span>
                    </div>
                    <div className="text-[#E2DFD8]">{m.body}</div>
                  </div>
                ))}
              </div>
              {selected.status !== "closed" && (
                <div className="flex border-t border-[#262626]">
                  <input
                    data-testid="ticket-reply-input"
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendReply()}
                    placeholder="Reply..."
                    maxLength={2000}
                    className="flex-1 bg-transparent px-3 py-2 text-xs text-[#E2DFD8] outline-none placeholder-[#555]"
                  />
                  <button
                    data-testid="ticket-reply-send"
                    onClick={sendReply}
                    disabled={busy || !reply.trim()}
                    className="px-3 border-l border-[#262626] text-[#E5C34A] hover:bg-[#1a1a1a] disabled:opacity-30"
                  >
                    <Send className="w-3 h-3" strokeWidth={1.5} />
                  </button>
                </div>
              )}
            </div>
          ) : null}

          {/* New ticket form */}
          <div className="panel p-3 space-y-2" data-testid="ticket-new">
            <div className="font-heading text-[10px] tracking-[0.3em] uppercase text-[#8c8c8c]">
              Open New Ticket
            </div>
            <input
              data-testid="ticket-subject"
              value={newSubj}
              onChange={(e) => setNewSubj(e.target.value)}
              placeholder="Subject"
              maxLength={120}
              className="w-full bg-[#0a0a0a] border border-[#262626] px-2 py-1.5 text-xs text-[#E2DFD8] outline-none focus:border-[#B89C3A]"
            />
            <textarea
              data-testid="ticket-body"
              value={newBody}
              onChange={(e) => setNewBody(e.target.value)}
              placeholder="Describe the situation..."
              maxLength={2000}
              rows={3}
              className="w-full bg-[#0a0a0a] border border-[#262626] px-2 py-1.5 text-xs text-[#E2DFD8] outline-none resize-none focus:border-[#B89C3A]"
            />
            <button
              data-testid="ticket-submit"
              onClick={openTicket}
              disabled={busy || !newSubj.trim() || !newBody.trim()}
              className="w-full py-2 border border-[#B89C3A] text-[#E5C34A] font-heading text-xs tracking-widest uppercase disabled:opacity-30"
            >
              Send Ticket
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
